import { BackButton, BREAKPOINTS, COLORS, Size, TextButton as Button, TextButtonProps as ButtonProps, SystemIcons, TextButton } from '@laerdal/life-react-components';
import { FC, ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Stepper from './Stepper';

const getScrollableAncestor = (node: HTMLElement | null): HTMLElement | Window => {
  let current: HTMLElement | null = node?.parentElement ?? null;
  while (current) {
    const { overflowY } = window.getComputedStyle(current);
    const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
    if (isScrollable && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return window;
};

const scrollWizardToTop = (node: HTMLElement | null) => {
  const target = getScrollableAncestor(node);
  target.scrollTo({ top: 0, behavior: 'smooth' });
};

const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 16px 32px 16px;
  
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-self: stretch;
  gap: 16px;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  font-family: Lato;
  color: ${COLORS.black};
  line-height: 1.2;
  margin: 0;

  ${BREAKPOINTS.LARGE} {
    font-size: 30px;
  }
`;

const Description = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${COLORS.neutral_600};
  margin: 0;
  line-height: 1.5;

  ${BREAKPOINTS.LARGE} {
    font-size: 15px;
  }
`;

const StepperSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items:flex-start;
  gap: 32px;
  align-self: stretch;

  ${BREAKPOINTS.LARGE} {
    flex-direction: row;
    gap: 32px;
    flex: 1;
    align-items:stretch;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex: 1;
  overflow-y: auto;
  margin-bottom: 24px;
  align-self: center;
  background-color: ${COLORS.neutral_20};
  border-radius: 8px;
  padding: 24px;

  ${BREAKPOINTS.LARGE} {
    flex: 1 0 0;
    align-self: stretch;
    align-items: flex-start;
  }
`;

const ActionsSection = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;

const HeaderImage = styled.img`
  width: 72px;
  height: 40px;
  object-fit: contain;
  flex-shrink: 0;

  ${BREAKPOINTS.LARGE} {
    width: 128px;
    height: 72px;
  }
`;

interface StepperWizardProps {
  title: string;
  description?: string;
  imageUrl?: string | null;
  steps: string[];
  currentStep: number;
  onBack?: () => void;
  children: ReactNode;
  actions?: ButtonProps[];
  showBackButton?: boolean;
  showLastStep?: boolean;
  ariaLabel?: string;
}

const StepperWizard: FC<StepperWizardProps> = ({
  title,
  description,
  imageUrl,
  steps,
  currentStep,
  onBack,
  children,
  actions = [],
  showBackButton = true,
  showLastStep = false,
  ariaLabel,
}) => {
  const { t } = useTranslation('landingPage');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollWizardToTop(containerRef.current);
  }, [currentStep]);

  return (
    <WizardContainer id="stepper-wizard" ref={containerRef}>
      {showBackButton && (
        <BackButton size={Size.Small} onClick={onBack}>
          {t('Back')}
        </BackButton>
      )}
      <Header>
        <HeaderContent>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
        </HeaderContent>
        {/* {imageUrl && <HeaderImage src={imageUrl} alt={title} />} */}
      </Header>
      <StepperSection>
        <Stepper
          steps={showLastStep ? steps : steps.slice(0, -1)}
          currentStep={currentStep}
          ariaLabel={ariaLabel || `${title} progress`}
        />
        <ContentSection>
          {children}
        </ContentSection>
      </StepperSection>
      {actions.length > 0 && (
        <ActionsSection>
          {actions.map((actionProps, index) => {
            const { onClick, ...rest } = actionProps;
            const handleClick: ButtonProps['onClick'] = (event) => {
              onClick?.(event);
              scrollWizardToTop(containerRef.current);
            };
            return (
              <Button
                key={index}
                {...rest}
                onClick={handleClick}>
                {actionProps.title}
              </Button>
            );
          })}
        </ActionsSection>
      )}
    </WizardContainer>
  );
};

export default StepperWizard;
